/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { AlertCircle, ArrowLeft, RefreshCw, SearchX } from "lucide-react";
import { Link, isRouteErrorResponse, useLocation, useRouteError } from "react-router";
import { theme } from "../styles/theme/theme";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from "@atomic/ui-kit";
// Branded replacement for React Router's default "Hey developer 👋" error
// screen. Serves two router slots: the root errorElement (render/loader
// errors and thrown 404 responses) and the "*" catch-all route, where
// useRouteError() is nullish (null, the context default, outside an error
// boundary) — an unmatched URL is by definition a 404.
export function RouteErrorView() {
  const error = useRouteError();
  const { pathname } = useLocation();

  const isNotFound = error == null || (isRouteErrorResponse(error) && error.status === 404);

  if (error != null && !isNotFound) {
    console.error("RouteErrorView caught a routing error:", error);
  }

  return (
    <div
      css={css`
        width: 100%;
        min-height: 100%;
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
      `}
    >
      <Card
        css={css`
          width: 100%;
          max-width: 28rem;
        `}
      >
        <CardHeader>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 0.75rem;
            `}
          >
            {isNotFound ? (
              <SearchX
                css={css`
                  width: 1.5rem;
                  height: 1.5rem;
                  color: ${theme.colors.mutedForeground};
                `}
              />
            ) : (
              <AlertCircle
                css={css`
                  width: 1.5rem;
                  height: 1.5rem;
                  color: ${theme.colors.statusError};
                `}
              />
            )}
            <CardTitle>{isNotFound ? "Page not found" : "Something went wrong"}</CardTitle>
          </div>
          <CardDescription>
            {isNotFound ? (
              <>
                The requested page <code>{pathname}</code> does not exist. It may have moved, or
                the link may be out of date.
              </>
            ) : (
              "An unexpected error occurred while loading this page. Try refreshing, or head back to the dashboard."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent
          css={css`
            display: flex;
            gap: 0.75rem;
            flex-wrap: wrap;
          `}
        >
          <Button asChild variant="primary">
            <Link to="/">
              <ArrowLeft
                css={css`
                  width: 1rem;
                  height: 1rem;
                `}
              />
              Back to dashboard
            </Link>
          </Button>
          {!isNotFound && (
            <Button onClick={() => window.location.reload()} variant="tertiaryOutline">
              <RefreshCw
                css={css`
                  width: 1rem;
                  height: 1rem;
                `}
              />
              Refresh page
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
